<script setup lang="ts">
import getYouTubeID from 'get-youtube-id'

// Props definition
interface Props {
  height?: number | string
  host?: string
  playerClasses?: string
  src: string
  vars?: YT.PlayerVars
  width?: number | string
}

const props = withDefaults(
  defineProps<Props>(),
  {
    height: 360,
    host: 'https://www.youtube.com',
    playerClasses: '',
    width: 640,
  },
)

// Define emits
const emit = defineEmits<{
  apiChange: [event: YT.PlayerEvent]
  error: [event: YT.OnErrorEvent]
  playbackQualityChange: [event: YT.OnPlaybackQualityChangeEvent]
  playbackRateChange: [event: YT.OnPlaybackRateChangeEvent]
  ready: [event: YT.PlayerEvent]
  stateChange: [event: YT.OnStateChangeEvent]
}>()

// Refs
const youtubeRef = ref<HTMLElement>()
const player = ref<null | YT.Player>(null)
const ready = ref(false)
const initiated = ref(false)

// Computed
const videoId = computed(() => {
  return getYouTubeID(props.src) || props.src
})

const wrapperStyle = computed(() => ({
  height: `${props.height}px`,
  position: 'relative' as const,
  width: `${props.width}px`,
}))

// Player state constants
const PlayerState = {
  BUFFERING: 3,
  CUED: 5,
  ENDED: 0,
  PAUSED: 2,
  PLAYING: 1,
  UNSTARTED: -1,
} as const

// Wait for YouTube API to be ready
function waitForYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve()

      return
    }

    // Set up the callback for when the API is ready
    if (!window.onYouTubeIframeAPIReadyResolvers) {
      window.onYouTubeIframeAPIReadyResolvers = []
    }

    if (!window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => {
        window.onYouTubeIframeAPIReadyResolvers?.forEach((resolver: () => void) => {
          resolver()
        })
      }
    }

    window.onYouTubeIframeAPIReadyResolvers.push(resolve)
  })
}

// Initialize the player
async function initPlayer() {
  if (!youtubeRef.value || initiated.value) {
    return
  }

  initiated.value = true

  player.value = new window.YT.Player(youtubeRef.value, {
    events: {
      onApiChange: (event: YT.PlayerEvent) => emit('apiChange', event),
      onError: (event: YT.OnErrorEvent) => emit('error', event),
      onPlaybackQualityChange: (event: YT.OnPlaybackQualityChangeEvent) => emit('playbackQualityChange', event),
      onPlaybackRateChange: (event: YT.OnPlaybackRateChangeEvent) => emit('playbackRateChange', event),
      onReady: (event: YT.PlayerEvent) => {
        ready.value = true
        nextTick(() => emit('ready', event))
      },
      onStateChange: (event: YT.OnStateChangeEvent) => emit('stateChange', event),
    },
    height: props.height,
    host: props.host,
    playerVars: props.vars,
    videoId: videoId.value,
    width: props.width,
  })
}

// Player control methods
function cueVideoById(videoId: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality) {
  player.value?.cueVideoById(videoId, startSeconds, suggestedQuality)
}

function loadVideoById(videoId: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality) {
  player.value?.loadVideoById(videoId, startSeconds, suggestedQuality)
}

function cueVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality) {
  player.value?.cueVideoByUrl(mediaContentUrl, startSeconds, suggestedQuality)
}

function loadVideoByUrl(mediaContentUrl: string, startSeconds?: number, suggestedQuality?: YT.SuggestedVideoQuality) {
  player.value?.loadVideoByUrl(mediaContentUrl, startSeconds, suggestedQuality)
}

const playVideo = () => player.value?.playVideo()
const pauseVideo = () => player.value?.pauseVideo()
const stopVideo = () => player.value?.stopVideo()
const seekTo = (seconds: number, allowSeekAhead: boolean) => player.value?.seekTo(seconds, allowSeekAhead)
const mute = () => player.value?.mute()
const unMute = () => player.value?.unMute()
const isMuted = () => player.value?.isMuted() ?? false
const setVolume = (volume: number) => player.value?.setVolume(volume)
const getVolume = () => player.value?.getVolume() ?? 0
const getPlaybackRate = () => player.value?.getPlaybackRate() ?? 0
const setPlaybackRate = (suggestedRate: number) => player.value?.setPlaybackRate(suggestedRate)
const getAvailablePlaybackRates = () => player.value?.getAvailablePlaybackRates() ?? []
const getPlayerState = () => player.value?.getPlayerState() ?? PlayerState.UNSTARTED
const getCurrentTime = () => player.value?.getCurrentTime() ?? 0
const getDuration = () => player.value?.getDuration() ?? 0

// Expose methods to parent component
defineExpose({
  cueVideoById,
  cueVideoByUrl,
  getAvailablePlaybackRates,
  getCurrentTime,
  getDuration,
  getPlaybackRate,
  getPlayerState,
  getVolume,
  isMuted,
  loadVideoById,
  loadVideoByUrl,
  mute,
  pauseVideo,
  player: readonly(player),
  playVideo,
  ready: readonly(ready),
  seekTo,
  setPlaybackRate,
  setVolume,
  stopVideo,
  unMute,
})

// Watchers
watch(() => props.width, () => {
  if (player.value) {
    player.value.setSize(Number(props.width), Number(props.height))
  }
})

watch(() => props.height, () => {
  if (player.value) {
    player.value.setSize(Number(props.width), Number(props.height))
  }
})

watch(() => props.src, () => {
  if (initiated.value && player.value) {
    player.value.loadVideoById(videoId.value)
  }
})

// Lifecycle
onMounted(async () => {
  await waitForYouTubeAPI()
  await initPlayer()
})

onBeforeUnmount(() => {
  player.value?.destroy()
})
</script>

<template>
  <div
    :class="playerClasses"
    :style="wrapperStyle"
  >
    <div
      ref="youtubeRef"
      :style="wrapperStyle"
    />
  </div>
</template>
